/**
 * configurator__aeroo.js — Path B
 * ---------------------------------------------------------------------------
 * Hybrid swap configurator for the Aeroo Pro family.
 *
 * On selection change:
 *   Instant (0ms):  update title, price, gallery (hero + all slides + thumbs),
 *                   dispatch/preorder message directly from matrix JSON.
 *                   Hidden variant ID, URL, bottom bar all updated
 *                   synchronously.
 *   Settle (~150ms): Section Rendering API fetch -> update description
 *                    (panel-2), features (panel-3), specs (panel-4), price
 *                    block, product-modal (zoom/lightbox).
 *                    Gallery and dispatch message are NOT touched by the
 *                    fetch — they're already done instantly from matrix data.
 *
 * Intercepts family chooser clicks. Drives add-on toggles. Updates URL via
 * pushState. Publishes PUB_SUB_EVENTS.variantChange so <product-info>
 * updates quantity rules.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var matrixEl = document.getElementById('aeroo-matrix');
  if (!matrixEl) {
    console.warn('[Aeroo configurator] #aeroo-matrix not found — serializer not rendered?');
    return;
  }

  var matrix;
  try {
    matrix = JSON.parse(matrixEl.textContent);
  } catch (err) {
    console.error('[Aeroo configurator] matrix JSON parse failed:', err);
    return;
  }

  /* ------------------ resolver ------------------ */
  function keyOf(s) {
    return s.version + '|' + s.controller + '|' + s.obstacle_avoidance;
  }

  function createResolver(data) {
    var idx = new Map();
    var byHandle = new Map();
    var recs = [];
    data.permutations.forEach(function (p) {
      var r = {
        version: p.version,
        controller: p.controller,
        obstacle_avoidance: p.obstacle_avoidance,
        handle: p.handle,
        product_id: p.product_id,
        title: p.title,
        available: p.available !== false,
        price: p.price,
        price_formatted: p.price_formatted,
        dispatch_message: p.dispatch_message || '',
        featured_image: p.featured_image,
        images: p.images || [],
        variants: p.variants || {},
      };
      idx.set(keyOf(r), r);
      if (r.handle) byHandle.set(r.handle, r);
      recs.push(r);
    });
    var dl = (data.default_locale || 'AU').toUpperCase();
    function variantFor(r) {
      var L = dl;
      if (r.variants[L] != null) return r.variants[L];
      var vs = Object.values(r.variants);
      return vs.length ? vs[0] : null;
    }
    function shape(r, requested, isFallback) {
      if (!r) {
        return {
          available: false, isFallback: true, requested: requested,
          handle: null, variantId: null,
          dispatchMessage: '',
        };
      }
      return {
        requested: requested,
        matched: { version: r.version, controller: r.controller, obstacle_avoidance: r.obstacle_avoidance },
        isFallback: isFallback,
        available: r.available,
        handle: r.handle, title: r.title, productId: r.product_id,
        variantId: variantFor(r),
        price: r.price, priceText: r.price_formatted,
        dispatchMessage: r.dispatch_message || '',
        featured: r.featured_image, images: r.images,
        productUrl: '/products/' + r.handle,
        sectionUrl: '/products/' + r.handle + '?section_id=' + (data.section_id || 'main-product'),
      };
    }
    return {
      resolve: function (sel) {
        var want = {
          version: sel.version,
          controller: sel.controller,
          obstacle_avoidance: sel.obstacle_avoidance,
        };
        var tries = [
          want,
          Object.assign({}, want, { obstacle_avoidance: 'none' }),
          Object.assign({}, want, { controller: 'standard' }),
          Object.assign({}, want, { controller: 'standard', obstacle_avoidance: 'none' }),
        ];
        for (var i = 0; i < tries.length; i++) {
          var r = idx.get(keyOf(tries[i]));
          if (r && r.available) return shape(r, want, i !== 0);
        }
        var ex = idx.get(keyOf(want));
        return ex ? shape(ex, want, false) : shape(null, want, true);
      },
      availability: function (partial) {
        var reach = { version: new Set(), controller: new Set(), obstacle_avoidance: new Set() };
        recs.forEach(function (r) {
          if (!r.available) return;
          if (partial.version != null && r.version !== partial.version) return;
          if (partial.controller != null && r.controller !== partial.controller) return;
          if (partial.obstacle_avoidance != null && r.obstacle_avoidance !== partial.obstacle_avoidance) return;
          reach.version.add(r.version);
          reach.controller.add(r.controller);
          reach.obstacle_avoidance.add(r.obstacle_avoidance);
        });
        return {
          version: Array.from(reach.version),
          controller: Array.from(reach.controller),
          obstacle_avoidance: Array.from(reach.obstacle_avoidance),
        };
      },
      fromHandle: function (handle) {
        var r = byHandle.get(handle);
        return r ? { version: r.version, controller: r.controller, obstacle_avoidance: r.obstacle_avoidance } : null;
      },
    };
  }

  var resolver = createResolver(matrix);

  /* ------------------ initial state ------------------ */
  var currentHandle = window.location.pathname.replace(/^\/products\//, '').split('?')[0];
  var initial = resolver.fromHandle(currentHandle);
  var sel = initial || {
    version: matrix.options.version[0],
    controller: 'standard',
    obstacle_avoidance: 'none',
  };
  var lastResolved = null;
  var swapInFlight = null;

  /* ------------------ gallery painting (from matrix, no network) ------------------ */

  /* Hero (slide 0): pre-decode then swap. Instant once decode resolves
     (microtask + decode cost — usually <16ms on a cached image). */
  function paintHero(r) {
    if (!r.featured) return;

    // Pick the size that matches the current viewport, so the pre-decode
    // matches what the browser will resolve from srcset. DPR-aware.
    var feat = r.featured;
    var src;
    if (typeof feat === 'string') {
      src = feat;
    } else {
      var dpr = window.devicePixelRatio || 1;
      var vw = window.innerWidth;
      var heroDisplayWidth = vw >= 1230 ? vw * 0.5 : vw; // matches the 'sizes' attribute
      var needed = heroDisplayWidth * dpr;
      if      (needed <= 750)   src = feat.src750;
      else if (needed <= 1100)  src = feat.src1100;
      else if (needed <= 1400)  src = feat.src1400;
      else if (needed <= 1920)  src = feat.src1920;
      else if (needed <= 2400)  src = feat.src2400;
      else                      src = feat.src3000;
    }

    var heroImg = document.querySelector('aeroo-gallery .aeroo-gallery__slide:first-child .aeroo-gallery__image');
    if (!heroImg || heroImg.src === src) return;
    var tmp = new Image();
    tmp.src = src;
    tmp.decode().catch(function(){}).then(function(){
      var stillThere = document.querySelector('aeroo-gallery .aeroo-gallery__slide:first-child .aeroo-gallery__image');
      if (stillThere) {
        stillThere.src = src;
        stillThere.srcset = '';
      }
    });
  }

/* Remaining slides + thumbnails: delegate to the gallery custom element,
     which rebuilds its DOM to match the new image count and runs a fade
     animation on the thumbnails. The hero is already painted by paintHero. */
  function paintGallery(r) {
    var gallery = document.querySelector('aeroo-gallery');
    if (!gallery || !r.images || !r.images.length) return;

    if (typeof gallery.rebuildFromImages === 'function') {
      gallery.rebuildFromImages(r.images);
    }
  }

  /* Dispatch/preorder message — driven entirely from matrix data (the
     dispatch_message field is pre-rendered server-side by test-ship-date.liquid
     via aeroo-matrix.liquid, so there is zero date logic or product-handle
     branching duplicated here). Requires a `data-aeroo-dispatch-text`
     attribute on the target element in the preorder markup. */
  function syncDispatchMessage(r) {
    if (typeof r.dispatchMessage !== 'string') return;
    document.querySelectorAll('[data-aeroo-dispatch-text]').forEach(function (el) {
      el.textContent = r.dispatchMessage;
    });
  }

  /* ------------------ instant updates from matrix ------------------ */
  function applyInstant(r) {
    /* update Title */
    var parts = r.title.split(' with');
    var mainTitle = parts[0];
    var caption = parts.length > 1 ? 'with' + parts[1] : '';

    // Visible h2: gets the split, "clean" title
    document.querySelectorAll('.product__title h2').forEach(function (el) {
      el.textContent = mainTitle;
    });

    // Hidden h2 (inside the display:none link): keep full real title
    document.querySelectorAll('a.product__title h2').forEach(function (el) {
      el.textContent = r.title;
    });

    // Caption: create/update/remove based on whether title has a "with" part
    document.querySelectorAll('div.product__title').forEach(function (titleBlock) {
      var captionEl = titleBlock.querySelector('.configurator__sc-caption');
      if (caption) {
        if (!captionEl) {
          captionEl = document.createElement('p');
          captionEl.className = 'configurator__sc-caption';
          var h2 = titleBlock.querySelector('h2');
          if (h2) h2.insertAdjacentElement('afterend', captionEl);
        }
        captionEl.textContent = caption;
      } else if (captionEl) {
        captionEl.remove();
      }
    });

    /* update link href */
    document.querySelectorAll('a.product__title').forEach(function (el) {
      el.href = r.productUrl;
    });

    /* update breadcrumbs */
    document.querySelectorAll('.breadcrumbs__list li:last-child a').forEach(function (el) {
      el.textContent = r.title;
      el.setAttribute('href', r.productUrl);
    });

    /* update bottom bar title, price, variant input */
    document.querySelectorAll('[data-bottom-bar-title]').forEach(function (el) {
      el.textContent = r.title;
    });
    document.querySelectorAll('[data-bottom-bar-price]').forEach(function (el) {
      el.textContent = r.priceText;
    });
    document.querySelectorAll('[id^="bottom-bar-"] input[name="id"]').forEach(function (el) {
      el.value = r.variantId;
    });
    document.querySelectorAll('[id^="bottom-bar-"] input[name="product-id"]').forEach(function (el) {
      el.value = r.productId;
    });

    var priceEls = document.querySelectorAll('[id^="price-"]');
    priceEls.forEach(function (el) { el.innerHTML = '<span class="price price-item price--large">' + r.priceText + ' NZD</span>'; });

    /* Afterpay / Square on-site messaging. Matrix price is in cents; Square's data-amount is in dollars → divide by 100. */
    if (r.price != null) {
      document.querySelectorAll('square-placement').forEach(function (el) {
        el.setAttribute('data-amount', String(r.price / 100));
      });
    }

    document.querySelectorAll('input[name="id"]').forEach(function (input) {
      input.value = r.variantId;
    });

    if (window.history && window.history.pushState) {
      window.history.pushState({ handle: r.handle }, '', r.productUrl);
    }

    /* Dispatch/preorder message — instant, matrix-driven, no hardcoding. */
    syncDispatchMessage(r);

    /* Gallery — driven entirely from matrix data, no network involved. */
    paintHero(r);
    paintGallery(r);
  }

  /* ------------------ Section Rendering API swap (non-gallery content) ------------------ */
  function performSectionSwap(r) {
    if (swapInFlight) swapInFlight.abort();
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    swapInFlight = controller;

    return fetch(r.sectionUrl, { signal: controller ? controller.signal : undefined })
      .then(function (res) {
        if (!res.ok) throw new Error('Section fetch failed: HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        /* Description / features / specs panels */
        replaceById(doc, 'panel-2');
        replaceById(doc, 'panel-3');
        replaceById(doc, 'panel-4');

        /* Price block (in case anything beyond what applyInstant updated needs refreshing) */
        var newPrice = doc.querySelector('[id^="price-"]');
        if (newPrice) {
          var currentPrice = document.getElementById(newPrice.id);
          if (currentPrice) currentPrice.innerHTML = newPrice.innerHTML;
        }

        /* Afterpay amount — take the server-rendered value as the source of truth. */
        var freshAP = doc.querySelector('square-placement');
        if (freshAP && freshAP.getAttribute('data-amount')) {
          var amt = freshAP.getAttribute('data-amount');
          document.querySelectorAll('square-placement').forEach(function (el) {
            if (el.getAttribute('data-amount') !== amt) el.setAttribute('data-amount', amt);
          });
        }

        /* Gallery is NOT touched here — paintHero + paintGallery handled it
           synchronously from matrix data in applyInstant. */

        /* Dispatch/preorder message is NOT touched here either — synced
           instantly from matrix data in applyInstant via syncDispatchMessage. */

        /* Zoom/lightbox modal — still needs the server-rendered version since
           it references media IDs that don't live in the matrix. */
        var freshModal = doc.querySelector('product-modal');
        var currentModal = document.querySelector('product-modal');
        if (freshModal && currentModal && currentModal.parentNode) {
          currentModal.parentNode.replaceChild(freshModal, currentModal);
        }

        document.dispatchEvent(new CustomEvent('aeroo:product-swapped', {
          detail: { handle: r.handle, variantId: r.variantId, productId: r.productId }
        }));

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: matrix.section_id || 'main-product', variant: { id: r.variantId } }
          });
        }

        swapInFlight = null;
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        console.error('[Aeroo configurator] section swap failed:', err);
        swapInFlight = null;
      });
  }

  function replaceById(doc, id) {
    var fresh = doc.getElementById(id);
    var current = document.getElementById(id);
    if (fresh && current) current.innerHTML = fresh.innerHTML;
  }
  function replaceQuerySelector(doc, selector) {
    var fresh = doc.querySelector(selector);
    var current = document.querySelector(selector);
    if (fresh && current) current.outerHTML = fresh.outerHTML;
  }

  // LOADER
  function setSwapping(on) {
    var info = document.querySelector('.product__info-container');
    if (info) info.classList.toggle('is-product-swapping', on);
  }

  /* ------------------ selection + render ------------------ */
  function applySelection(next) {
      Object.assign(sel, next);
      var r = resolver.resolve(sel);
      if (!r.available) {
        console.warn('[Aeroo configurator] selection unavailable', sel);
        return;
      }
      syncAddonVisibility();
      if (lastResolved && lastResolved.handle === r.handle && lastResolved.variantId === r.variantId) {
        syncAddonChecked();
        return;
      }
      lastResolved = r;
      setSwapping(true);
      applyInstant(r);
      syncFamilyChooserActive();
      syncAddonChecked();
      performSectionSwap(r).then(function () {
        setSwapping(false);
      });
    }
  // function applySelection(next) {
  //   Object.assign(sel, next);
  //   var r = resolver.resolve(sel);
  //   if (!r.available) {
  //     console.warn('[Aeroo configurator] selection unavailable', sel);
  //     return;
  //   }
  //   syncAddonVisibility();
  //   if (lastResolved && lastResolved.handle === r.handle && lastResolved.variantId === r.variantId) {
  //     syncAddonChecked();
  //     return;
  //   }
  //   lastResolved = r;
  //   applyInstant(r);
  //   syncFamilyChooserActive();
  //   syncAddonChecked();
  //   performSectionSwap(r);
  // }

  /* Marks the family chooser pill active based on the currently-resolved
     version (AP, APC, etc), not the permutation handle. This way toggling
     SC or OA keeps the right family pill active even though the resolved
     handle changes. Reads data-version on each [data-aeroo-family-link]. */
  function syncFamilyChooserActive() {
    var r = lastResolved;
    if (!r) return;
    var currentVersion = r.matched.version;

    var links = document.querySelectorAll('[data-aeroo-family-link]');
    links.forEach(function (link) {
      var section = link.closest('[data-aeroo-family-chooser]');
      var sectionMatch = section ? section.className.match(/ChooseProduct__Aeroo__([^\s]+)/) : null;
      var sectionId = sectionMatch ? sectionMatch[1] : '';
      var activeClass = 'VariantSelect_' + sectionId + '_active';
      var disabledClass = 'VariantSelect_' + sectionId + '_disabled';

      if (link.getAttribute('data-version') === currentVersion) {
        link.classList.add(activeClass);
        link.classList.remove(disabledClass);
      } else {
        link.classList.remove(activeClass);
        link.classList.add(disabledClass);
      }
    });
  }

  function syncAddonVisibility() {
    var avail = resolver.availability({ version: sel.version });
    document.querySelectorAll('[data-aeroo-addon]').forEach(function (row) {
      var addon = row.getAttribute('data-aeroo-addon');
      var visible = false;
      if (addon === 'smart_controller') visible = avail.controller.indexOf('smart') !== -1;
      if (addon === 'obstacle_avoidance') visible = avail.obstacle_avoidance.indexOf('included') !== -1;
      row.classList.toggle('is-visible', visible);
    });
  }

  function syncAddonChecked() {
    document.querySelectorAll('[data-aeroo-addon-toggle="smart_controller"]').forEach(function (sc) {
      sc.checked = sel.controller === 'smart';
    });
    document.querySelectorAll('[data-aeroo-addon-toggle="obstacle_avoidance"]').forEach(function (oa) {
      oa.checked = sel.obstacle_avoidance === 'included';
    });
  }

  /* ------------------ wire up event listeners ------------------ */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-aeroo-family-link]');
    if (!link) return;
    var handle = link.getAttribute('data-handle');
    if (!handle) return;
    var recovered = resolver.fromHandle(handle);
    if (!recovered) return;
    e.preventDefault();
    applySelection({
      version: recovered.version,
      controller: sel.controller,
      obstacle_avoidance: sel.obstacle_avoidance,
    });
  });

  document.addEventListener('change', function (e) {
    var toggle = e.target.closest('[data-aeroo-addon-toggle]');
    if (!toggle) return;
    var addon = toggle.getAttribute('data-aeroo-addon-toggle');
    if (addon === 'smart_controller') {
      applySelection({ controller: toggle.checked ? 'smart' : 'standard' });
    } else if (addon === 'obstacle_avoidance') {
      applySelection({ obstacle_avoidance: toggle.checked ? 'included' : 'none' });
    }
  });

  window.addEventListener('popstate', function () {
      var handle = window.location.pathname.replace(/^\/products\//, '').split('?')[0];
      var recovered = resolver.fromHandle(handle);
      if (!recovered) return;
      Object.assign(sel, recovered);
      var r = resolver.resolve(sel);
      if (r.available) {
        lastResolved = r;
        setSwapping(true);
        applyInstant(r);
        syncAddonVisibility();
        syncAddonChecked();
        syncFamilyChooserActive();
        performSectionSwap(r).then(function () {
          setSwapping(false);
        });
      }
    });
  // window.addEventListener('popstate', function () {
  //   var handle = window.location.pathname.replace(/^\/products\//, '').split('?')[0];
  //   var recovered = resolver.fromHandle(handle);
  //   if (!recovered) return;
  //   Object.assign(sel, recovered);
  //   var r = resolver.resolve(sel);
  //   if (r.available) {
  //     lastResolved = r;
  //     applyInstant(r);
  //     syncAddonVisibility();
  //     syncAddonChecked();
  //     syncFamilyChooserActive();
  //     performSectionSwap(r);
  //   }
  // });

  /* ------------------ initial sync ------------------ */
  syncAddonVisibility();
  syncAddonChecked();
  if (initial) {
    lastResolved = resolver.resolve(sel);
    syncFamilyChooserActive();
    syncDispatchMessage(lastResolved);
  }
})();